import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useAppState } from '../../app/state';
import { getSnapshot, type AppSnapshot } from '../../app/state';
import { callAction } from '../../app/actions';
import { anchors, cancelFlight, fly, supersede } from '../../app/flight';
import { getStoreState, useAppStore } from '../../store';
import { NodeChip } from '../../components/NodeChip';
import { getNodeDocPath } from '../../app/mapWrites';
import { toHex6 } from '../../lib/color';
import type { DocPlan, MapNode, NodeId, TagId } from '../../lib/types';
import { AiPanel, aiDraftOf, type AiDraft } from './AiPanel';
import { PopupDocs } from './PopupDocs';
import { CountPill, PopupFold } from './PopupFold';
import { TagPicker } from './TagPicker';
import { SECTION_DOCS, SECTION_RESPONSIBLES, SECTION_TAGS } from './sections';
import { TodoSection } from './TodoSection';
import { ProjectHead } from './ProjectHead';
import { registerHandle, type PopupHandle, type PopupOpenOptions } from '../../app/handles';
import { FavStar } from '../../components/FavStar';
import { ReslugButton } from '../../components/ReslugButton';
import { SubjectPicker, initialsOf } from '../../components/SubjectPicker';
import { WatchToggle } from '../feed/WatchToggle';
import { buildHiddenSets, canAssignTags, canEditMap, canEditTags, canEnterEdit } from '../../lib/permissions';
import { canAiAdminHere, capAi } from '../../lib/capabilities';
import { inEditMode } from '../../lib/mode';
import { canAssign, coveredByGroup, isPromptResponsible, responsibleSources } from '../../lib/responsibility';
import { DOC_BEAN_COLOR, DOC_KINDS, kindColorOf } from '../../lib/docs';
import { OWED_PILL_LABEL, plannedMissing } from '../../lib/health';

/**
 * The node popup — the last region of the vanilla app, and the last uncontrolled form in it.
 *
 * WHAT THE ORIGINAL DID, because it explains every decision below. `openPopup(id)` built a
 * ~60-line HTML string, wrote it into `#popup`, then queried the DOM it had just written to
 * attach handlers to it, and read its values back out of that same DOM on Save. The plan calls
 * this out by name as the one function that could not survive a mechanical port. So the values
 * are state here — held in `PopupBody` — and the Save button hands them to a vanilla action
 * rather than the vanilla side fetching them from elements.
 *
 * OPENING IS A REMOUNT; REFRESHING IS NOT. `PopupBody` is keyed on `id:seq`, so `open()` for
 * the node already showing remounts it and re-reads every value from the node — the faithful
 * expression of the vanilla `innerHTML` rebuild, as a key rather than an assignment.
 *
 * THE PROBLEM WAS THAT "REDRAW THIS POPUP" WAS SPELLED `openPopup(id)`. Places in
 * `infostation.html` called it to refresh an open popup — a target armed, AI-doc mode toggled,
 * an undo landing — and each rebuilt the markup and so discarded anything typed but not saved.
 * Faithfully ported, that made naming a node and toggling AI-doc mode lose the name, which is
 * what the V0.45 self-documentation walkthrough hit. Two of the four sites went in V0.42.6
 * (attaching and detaching a document — the only ones reachable from inside the popup itself,
 * so the ordinary act of attaching a page ate the form). The remaining ones are resolved by
 * giving the intent its own verb, in three layers:
 *
 *  - `refresh(id)` re-derives `ro` and `ai` for a popup already open on that node, keeping
 *    `seq`, so the body is not remounted. A landing undo calls it. (`setAiDocMode()` did
 *    too, until V0.50 took the AI panel off that mode — see `aiModeAtOpen`.)
 *  - Two sites needed nothing at all: the targets screen and a live-verification approval
 *    both read live state (`s.appTargets`, `node.liveObservationAt`), so `render()` is the
 *    whole update. `afterTargetsClosed()` existed only to re-open, and is gone.
 *  - A `dirty` guard routes a same-node `open()` to `refresh()` while a draft is unsaved.
 *    That is the part that does not depend on knowing every call site — including the ones
 *    nobody has written yet.
 *
 * `ro` AND `ai` ARE STILL CAPTURED AT OPEN, not derived live: the original chose its shape
 * when it built the markup, and toggling edit mode behind an open popup did not restyle it.
 * `refresh()` re-derives them at a moment somebody asked for, which is not the same thing as
 * following them continuously.
 *
 * THE EDIT SHAPE IS PER-BRICK SINCE V0.37. Any participant can open it, but each section is
 * drawn editable only behind its own gate — name/colour/description and the doc slots behind
 * `EDIT_MAP`, the tag chips behind `ASSIGN_TAGS`, the responsibles picker behind `canAssign`
 * (an effective responsible, or `EDIT_ALL_DOCS`), the AI panel per the note on `aiModeAtOpen`
 * below. A section whose brick is missing renders its READ shape instead, so the popup never
 * collects a value `savePopup` would have to drop and the server would refuse. A legacy
 * `SHOW_EDIT` role expands to every brick, so its popup is pixel-identical to V0.36's.
 *
 * TWO HOST ELEMENTS, ONE REGION. `#popup` is the region's host; `#scrim` is its sibling in the
 * page and stays vanilla markup, because a React root can own only what is inside its host and
 * portalling a second scrim into `<body>` would put two of them in the z-order. So this
 * component drives the scrim's `show` class and owns its click handler, and `infostation.html`
 * no longer listens on it.
 *
 * `popupFor` IS ANNOUNCED, NOT INFERRED. The vanilla script still needs to know which node the
 * popup is on: `myPresenceLoc()` reports it to the presence socket, and the Ctrl-hold "add
 * child" guard suppresses itself while a popup is open. Rather than leave it to be guessed at
 * from a class on an element React owns, the region tells it — `setPopupFor` — which is the
 * same shape as the camera's `mapGetCamera()`/`mapSetCamera()` seam the map left behind.
 */

/* The handle's shape is `PopupHandle` in `app/handles.ts`, where every region's is: the
   orchestration in `actions.ts` (`focusPopup`) needs the `open(id, {onSeat})` and
   `close({fly})` options, and a type it could only import from a feature would be the wrong
   direction. */

/**
 * Which shape of the AI panel this popup carries, decided AT OPEN like `ro`:
 *
 *  - `'admin'`  — this user is the AI operator (role AND capability): the full panel,
 *    mechanics included. Exactly the panel the popup has always had.
 *
 *    IT NO LONGER RIDES AI-DOC MODE. The panel used to require the top bar's AI
 *    button as well as the right, so the one surface where a node's prompt is written was
 *    behind a toggle that says nothing about itself — an operator who had not found the
 *    button saw a popup with no AI in it and no hint that there was any. The RIGHT is the
 *    gate now, which is what the right is for; AI-doc mode keeps its toolbar and its stage
 *    filter and stops being a precondition for reading a node's own settings. The panel
 *    opens COLLAPSED, so what it costs a popup that is not about the AI is one summary line.
 *
 *    SHIPPED IN V0.50, and recorded in the changelog only at V0.51 — the commit landed under
 *    a branch aiming at a version that was never cut, and its comments cited "V0.49.5" until
 *    then. Worth one line here because the dates in a comment are how somebody works out
 *    which release changed a behaviour they are looking at.
 *  - `'prompt'` — this user is in the node's RESPONSIBILITY scope (a responsible of the node
 *    or of one of its docs) and is NOT the AI operator: status and prompt only, which is the
 *    V0.37 point — the people who know the content reach the prompt field. Deliberately the
 *    scope alone, not `canEditPrompt`'s role overrides: folding EDIT_ALL_DOCS in would put an
 *    AI panel into every legacy editor's popup, and the macro's acceptance test is that their
 *    app does not change.
 *  - `null`     — no panel.
 */
type AiPanelMode = 'admin' | 'prompt' | null;

interface OpenState {
  id: NodeId;
  /** View-only shape. `openPopup()`'s `ro`, captured at open. */
  ro: boolean;
  /** Which AI panel this popup carries, captured at open. See `AiPanelMode`. */
  ai: AiPanelMode;
  /** Bumped on every open, so re-opening the SAME node still remounts the body. */
  seq: number;
}

function aiModeAtOpen(snap: AppSnapshot, id: NodeId): AiPanelMode {
  const operator = canAiAdminHere(snap.perm, snap.caps);
  if (operator) return 'admin';
  if (!operator && isPromptResponsible(snap, id)) return 'prompt';
  return null;
}

export function NodePopup({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [open, setOpen] = useState<OpenState | null>(null);
  const seq = useRef(0);
  /**
   * Has the person typed or clicked something in this popup that is not saved yet?
   *
   * Set by every user-driven draft setter in `PopupBody` (see `touch`), cleared on close. It
   * is the last line of defence for the draft: an `open()` for the node ALREADY SHOWING is
   * routed to `refresh()` while this is set, so a call site nobody predicted — a second
   * click, a mode toggle, whatever gets added next — cannot silently discard half a
   * paragraph. A remount on a clean popup is still a remount, which keeps `open()` honest
   * about being the verb that resets.
   */
  const dirty = useRef(false);
  /** Which node is open, readable from the handle callbacks without re-creating them. */
  const openId = useRef<NodeId | null>(null);

  /** The state half of closing: the popup is gone and the draft with it. */
  const finishClose = useCallback(() => {
    dirty.current = false;
    openId.current = null;
    setOpen(null);
  }, []);

  /**
   * Close — and with `fly`, put the node back on the map first (V0.47).
   *
   * The node came INTO this popup from the map, landing in the seat at the top; the region's
   * own closes (scrim, Cancel, Save, Escape) send it back the same way, the popup fading
   * under it, and only then unmount. A close that arrives while the open is still landing
   * reverses from wherever the clone is — `fly` reads the current flight for the same node —
   * which is why this bumps the sequence with `supersede()` and NOT `beginSequence()`, whose
   * finish would remove that clone first.
   *
   * Instant when asked plainly (the router, "node vanished"), when the node is not on the
   * stage to return to, or under reduced motion — where `fly` itself completes at once, so
   * the region behaves exactly as it did before any of this existed.
   */
  const close = useCallback(
    (opts?: { fly?: boolean }) => {
      const id = openId.current;
      const from = opts?.fly && id ? anchors.seat(id) : null;
      if (!id || !from || !anchors.mapNode(id)) {
        cancelFlight();
        getStoreState().setPerch(null);
        finishClose();
        return;
      }
      supersede();
      host.scrollTop = 0;
      document.getElementById('scrim')?.classList.remove('show');
      host.style.pointerEvents = 'none';
      fly({
        id,
        from,
        to: () => anchors.mapNode(id),
        land: null,
        fades: [{ el: host, from: 1, to: 0 }],
        onEnd: () => {
          host.style.pointerEvents = '';
          // SYNCHRONOUSLY. `finish()` clears the fade's inline opacity right after this
          // callback returns, and a plain `setOpen(null)` from a rAF callback is batched by
          // React 18 — so the popup came back at full opacity for the frame or two before the
          // commit removed `.show`: gone, back, gone. `flushSync` commits the unmount here, and
          // is safe here because a rAF callback is outside every render and effect.
          flushSync(finishClose);
        },
      });
    },
    [finishClose, host],
  );
  /** What the body's Cancel and Save call: the reader's own close, which flies. */
  const flyClose = useCallback(() => close({ fly: true }), [close]);

  /** Stable, so wrapping every draft setter in it does not churn on each render. */
  const markDirty = useCallback(() => {
    dirty.current = true;
  }, []);

  const api = useRef<PopupHandle>({ open: () => {}, refresh: () => {}, close: () => {} });
  /**
   * The `onSeat` callback of the open in progress, keyed by the `seq` it belongs to, so the
   * show effect can fire it exactly once for THAT mount and a refresh (same `seq`) never can.
   */
  const pendingSeat = useRef<{ seq: number; cb: (seat: HTMLElement | null) => void } | null>(null);

  /**
   * Re-read the popup's SHAPE — read-only vs editable, which AI panel — for a popup that is
   * already open on this node, keeping the same `seq` so `PopupBody` is NOT remounted and the
   * draft in it survives.
   *
   * This is what the four "re-open to refresh" call sites wanted. Two of them are gone
   * entirely (the targets screen and a live-verification approval, both of which read live
   * state already); of the other two, an undo landing calls this, and the AI-doc mode toggle
   * no longer needs to — V0.50 gated the panel on the right rather than the mode, so the
   * toggle changes nothing in here. The verb stays: the `dirty` guard routes through it.
   */
  api.current.refresh = useCallback((id: NodeId) => {
    const snap = getSnapshot();
    if (!snap.nodes[id] || openId.current !== id) return;
    setOpen((cur) =>
      cur && cur.id === id
        ? { ...cur, ro: !inEditMode(snap), ai: inEditMode(snap) ? aiModeAtOpen(snap, id) : null }
        : cur,
    );
  }, []);

  api.current.open = useCallback((id: NodeId, opts?: PopupOpenOptions) => {
    // Read the snapshot rather than the rendered `s`: `openPopup()` is called synchronously
    // from vanilla code that has just mutated state, and `getSnapshot()` is current from the
    // `sync()` those paths make. It also guards on the node existing, as the original did.
    const snap = getSnapshot();
    if (!snap.nodes[id]) {
      opts?.onSeat?.(null);
      return;
    }
    // A re-open of the node already showing, with unsaved work in it, is a refresh. See
    // `dirty` above for why this is a guard rather than a promise that no caller does it.
    // No fresh seat comes of it, and the caller is told so rather than left waiting.
    if (dirty.current && openId.current === id) {
      opts?.onSeat?.(null);
      return api.current.refresh(id);
    }
    // Past that point this IS a remount: a fresh body with a draft read from the node. So the
    // flag resets — otherwise opening a different node while the first had unsaved work left
    // `dirty` true over a form nobody had touched, and the NEXT open of that node would have
    // taken the refresh path for no reason.
    dirty.current = false;
    openId.current = id;
    seq.current += 1;
    pendingSeat.current = opts?.onSeat ? { seq: seq.current, cb: opts.onSeat } : null;
    setOpen({
      id,
      ro: !inEditMode(snap),
      ai: inEditMode(snap) ? aiModeAtOpen(snap, id) : null,
      seq: seq.current,
    });
  }, []);
  api.current.close = close;
  useEffect(() => {
    registerHandle('popup', api.current);
  }, []);

  /* The scrim: shown with the popup, and clicking it closes. Owned here because it is the
     popup's, even though the element itself is still the vanilla page's. */
  useEffect(() => {
    const scrim = document.getElementById('scrim');
    if (!scrim) return;
    scrim.addEventListener('click', flyClose);
    return () => scrim.removeEventListener('click', flyClose);
  }, [flyClose]);

  const node: MapNode | undefined = open ? s.nodes[open.id] : undefined;
  const shown = !!open && !!node;

  /**
   * The node genuinely went away — deleted by somebody else, or withheld by a permission
   * change (V0.43: the server omits nodes the caller may not see, so a refetch can simply
   * stop carrying one).
   *
   * `shown` already hides the popup in that case, but `open` stayed set, which left the
   * region in a state that is neither open nor closed: the scrim was down, `popupFor` still
   * named a node nobody could see, and a later `open()` for the same id would have taken the
   * dirty path. Close it properly instead.
   */
  useEffect(() => {
    if (open && !node) close();
  }, [open, node, close]);

  // A LAYOUT effect, not a passive one (V0.47): the seat is measured from here, and a
  // measurement taken after paint would first show the popup with an empty chair and THEN
  // start the flight into it. Before paint, `.show` lands and the clone departs in one frame.
  useLayoutEffect(() => {
    host.classList.toggle('show', shown);
    if (open) host.classList.toggle('readonly', open.ro);
    document.getElementById('scrim')?.classList.toggle('show', shown);
    if (shown) {
      // Near centre-top, as `openPopup()` positioned it.
      host.style.left = '50%';
      host.style.top = '90px';
      host.style.transform = 'translateX(-50%)';
      // The seat is at the top; a popup left scrolled would measure it clipped away.
      host.scrollTop = 0;
    }
    // Hand the fresh seat to the open that asked for it — once per mount. A refresh keeps the
    // `seq`, so it can never fire this again for the same popup.
    const pending = pendingSeat.current;
    if (shown && open && pending && pending.seq === open.seq) {
      pendingSeat.current = null;
      pending.cb(host.querySelector<HTMLElement>('.aidoc-node.seat'));
    }
  }, [shown, open, host]);

  /**
   * Tell the vanilla script which node is open.
   *
   * Only on a CHANGE of node, where the original called `sendLoc()` on every open: re-opening
   * the same node reports the same location, and the send is debounced anyway, so the wire
   * traffic is identical and the guard keeps this from firing before `boot()` has registered
   * any action.
   */
  const announced = useRef<NodeId | null>(null);
  useEffect(() => {
    const id = shown && open ? open.id : null;
    if (announced.current === id) return;
    announced.current = id;
    callAction('setPopupFor', id);
  }, [shown, open]);

  if (!open || !node) return null;

  return (
    <PopupBody
      key={`${open.id}:${open.seq}`}
      node={node}
      s={s}
      readOnly={open.ro}
      aiMode={open.ai}
      onClose={flyClose}
      onTouch={markDirty}
    />
  );
}

/* ------------------------------------------------------------------------------------ *
 * The body: everything that is DRAFT state.                                              *
 *                                                                                        *
 * Remounted per open (see the key above), so `useState`'s initialiser IS "read the node   *
 * when the popup opens" — which is exactly when the original read it.                     *
 * ------------------------------------------------------------------------------------ */

interface PopupBodyProps {
  node: MapNode;
  s: AppSnapshot;
  readOnly: boolean;
  aiMode: AiPanelMode;
  onClose: () => void;
  /** "The person has changed something in here." See `dirty` in `NodePopup`. */
  onTouch: () => void;
}

function PopupBody({ node, s, readOnly, aiMode, onClose, onTouch }: PopupBodyProps) {
  // Where the node is (V0.47): the seat at the top of this popup draws empty until the node
  // has landed in it, and empties again as it leaves for a page or for the map.
  const perch = useAppStore((st) => st.perch);
  const seatEmpty = !!perch && perch.id === node.id && perch.at !== 'seat';
  /* The per-brick shape (V0.37) — see the header. For a legacy SHOW_EDIT role every one of
     these is true and the markup is exactly V0.36's. */
  const structural = canEditMap(s.perm);
  const tagsEditable = canAssignTags(s.perm);
  const assignable = canAssign(s, node.id);
  /**
   * The responsibles row, in the EDITABLE body only (V0.49.1).
   *
   * Every participant may see it there — the payload carries the data for them — but the
   * read-only body no longer draws it at all, so this gates one place rather than two.
   */
  const showResp = canEnterEdit(s.perm);

  const [label, setLabel] = useState(node.label);
  const [desc, setDesc] = useState(node.desc || '');
  const [color, setColor] = useState(() => toHex6(node.color || '#5b8cff'));
  const [tags, setTags] = useState<ReadonlySet<TagId>>(() => new Set(node.tags || []));
  const [ai, setAi] = useState<AiDraft | null>(() =>
    aiMode ? aiDraftOf(node, aiMode === 'admin') : null,
  );
  /**
   * The DIRECT responsibles draft (V0.37, groups V0.37.1), captured at open like every other
   * value — only when the picker is drawn. Inherited entries are not in here: they belong to
   * an ancestor, and the read rows below the picker say so.
   *
   * `node.responsibles` arrives from the server ALREADY carrying an assigned group's members
   * (auth/responsibility.ts expands them for gating) — so the individual draft is seeded with
   * those members FILTERED OUT, or every save would silently re-write them as individual rows
   * the moment the group's chip was ever removed. `coveredByGroup()` is the same filter the
   * picker and the read-only view both use to decide "shown via the group, not on its own".
   *
   * THE SEED IS ONLY TRUSTWORTHY ONCE THE DIRECTORIES HAVE LOADED: before that,
   * `coveredByGroup()` sees an empty `groupDirectory` and filters nobody, so group members
   * would sit in the draft as individuals — and a save would persist them as personal rows.
   * The picker is inert until both directories are in (it renders a loading row), so no user
   * edit can exist before then; that makes the recovery safe: re-seed ONCE when they land
   * (`respSeedTrusted`), and until they have, `save()` omits the responsibles families
   * entirely — the server treats an omitted family as "leave it alone".
   */
  const respSeedTrusted = useRef(s.userDirectoryLoaded && s.groupDirectoryLoaded);
  const [responsibleGroups, setResponsibleGroups] = useState<string[]>(() => [
    ...(node.responsibleGroups ?? []),
  ]);
  const [responsibles, setResponsibles] = useState<string[]>(() => {
    const covered = coveredByGroup(node.responsibleGroups ?? [], s.groupDirectory);
    return (node.responsibles ?? []).filter((id) => !covered.has(id));
  });
  useEffect(() => {
    if (respSeedTrusted.current || !s.userDirectoryLoaded || !s.groupDirectoryLoaded) return;
    respSeedTrusted.current = true;
    setResponsibleGroups([...(node.responsibleGroups ?? [])]);
    const covered = coveredByGroup(node.responsibleGroups ?? [], s.groupDirectory);
    setResponsibles((node.responsibles ?? []).filter((id) => !covered.has(id)));
    // node.* deliberately not in the deps: this fires once, on the load edge, for the node
    // this body was keyed open on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.userDirectoryLoaded, s.groupDirectoryLoaded]);
  /**
   * The documentation-plan draft (V0.38), captured at open like every other value. `null` is
   * "no plan"; `{}` is "nothing expected"; keys are the plan. No directory dependency, so no
   * trust guard: the value seeds straight off the node, which every participant's payload
   * carries. Only drawn (and only saved) behind `canAssign` — planning a node's documentation
   * is the same authority as distributing it, the server's own gate.
   */
  const [docPlan, setDocPlan] = useState<DocPlan | null>(() =>
    node.expectedDocs === undefined || node.expectedDocs === null ? null : { ...node.expectedDocs },
  );
  /**
   * The documentation location inside the application (V0.46) — `null` until the field has
   * LOADED, which it does on the first interaction with it and never on open.
   *
   * Every other value in this body seeds from the node, because `/api/map` carries it. This one
   * does not: `docPath` is behind its own per-node route, and the popup counts its requests —
   * `e2e/34-doc-overlay` and `e2e/36-popup` both do — so a section that fetched on open would
   * add a request to every popup ever opened for a field almost nobody edits. That is the same
   * accident `ReviewControl`'s `seed` prop exists to prevent, and the route's own header says so.
   *
   * So absence here means "not loaded", `savePopup` writes nothing for it, and `from` records
   * what the server held so a value typed and reverted is not a write either.
   */
  const [docPath, setDocPath] = useState<{ path: string | null; from: string | null } | null>(null);

  /**
   * Wrap a draft setter so using it marks the popup dirty.
   *
   * Applied to the USER-DRIVEN setters only. The two programmatic re-seeds below — the
   * directories landing, and the AI-panel shape changing — deliberately do NOT go through it:
   * they are this component correcting itself, not somebody typing, and treating them as
   * edits would make a popup dirty that nobody has touched.
   */
  const touch = useCallback(
    <T,>(set: (v: T) => void) =>
      (v: T) => {
        onTouch();
        set(v);
      },
    [onTouch],
  );

  /**
   * The AI panel changed shape while this popup stayed mounted — `refresh()` recomputed
   * `aiMode` without remounting us.
   *
   * ONLY the AI draft is re-seeded, because only the AI draft's SHAPE depends on that value
   * ('admin' carries the generation mechanics, 'prompt' does not — see `aiDraftOf`). The name,
   * description, tags, responsibles and plan are untouched, which is the whole point: a shape
   * changing under somebody is not a reason to lose a paragraph they were writing.
   *
   * The AI-doc mode toggle was what moved this until V0.50; nothing routine does now, which
   * is why the guard stays rather than the effect going — a shape edge still has to be safe.
   */
  const aiSeeded = useRef(aiMode);
  useEffect(() => {
    if (aiSeeded.current === aiMode) return;
    aiSeeded.current = aiMode;
    setAi(aiMode ? aiDraftOf(node, aiMode === 'admin') : null);
    // `node` deliberately out of the deps: this fires on a MODE edge, for the node this body
    // is keyed open on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiMode]);

  /* The name at the moment the field took focus, so Escape can put it back. A ref, not state:
     nothing renders from it and a re-render per keystroke to track it would be waste. */
  const nameAtFocus = useRef(label);

  /* THE 30 ms AUTO-FOCUS OF `#pp-name` WENT WITH THE NAME LINE (V0.51). It existed because the
     first field of a form is where a form starts, and the first field was the name. The name is
     the seat now, and focusing it on open would put every popup into a rename — the reader has
     to look at the node before deciding to change it. Nothing pins the old behaviour. */

  // THE SEAT (V0.47). The header used to be the node's name as 22px display type beside a
  // 12px dot; it is now the node itself, drawn the way the map draws it — the same chip the
  // dashboard tables render — so that when the popup opens the node on the map can lift off
  // and land HERE, and be recognisably the same thing when it does. Painted from the STORED
  // colour, never the draft, as the dot was. `#pp-title` is still the label and nothing but
  // the label: the suite reads it by that id.
  //
  // The favorite star (plan 2.4) and the watch bell (plan Phase 3) follow it in the header
  // of BOTH shapes — `head` is shared, so two lines cover the read-only and the editable popup
  // alike. Star first, bell second: the pair reads the same here and in the doc bar.
  const head = (
    <h3>
      <NodeChip
        node={node}
        isLight={s.isLight}
        variant="seat"
        labelId="pp-title"
        dataId={node.id}
        empty={seatEmpty}
        /* V0.51: the name and the colour are edited HERE, in the node, rather than on two
           labelled lines below a seat that was already showing both. Only in the editable
           shape and only behind EDIT_MAP — the read popup's seat is the V0.47 chip unchanged,
           which is what `node-popup.png` photographs. The tint follows the DRAFT colour, so
           the chip answers the picker while somebody is choosing; it was the stored colour
           before, and there was no picker in the chip to answer. */
        {...(structural && !readOnly
          ? {
              pen: true,
              nameEditor: {
                value: label,
                onChange: touch(setLabel),
                onFocus: () => {
                  nameAtFocus.current = label;
                },
                onCancel: () => setLabel(nameAtFocus.current),
              },
              ...(node.root ? {} : { swatchInput: { value: color, onChange: touch(setColor) } }),
            }
          : {})}
      />
      {/* THE TOOLS CLUSTER RIGHT (V0.51). The order is unchanged — star, bell, re-slug, the
          project mark — and so is every id; what changed is that they are one group pushed to
          the far end by `margin-left:auto` instead of five things trailing the node across its
          own line. The seat is the node and reads as the node; the tools are tools. Nothing
          V0.50.3 decided about the head leaves it. `node-popup.png` is untouched: a wrapper
          around a star and a bell that were already adjacent moves neither. */}
      <span className="pp-headtools">
        <FavStar targetType="node" targetId={node.id} id="pp-fav" />
        <WatchToggle targetType="node" targetId={node.id} id="pp-watch" />
        {/* Draws nothing without RESLUG and Edit mode. */}
        <ReslugButton kind="node" targetId={node.id} drifted={!!node.slugDrifted} id="pp-reslug" />
        {/* The project flag, and when the node is one, its dates and the way to the Projects
            tab. Draws nothing for a non-project unless this reader may mark one. */}
        <ProjectHead node={node} s={s} readOnly={readOnly} />
      </span>
    </h3>
  );

  /* Permission-hidden tags, expanded down the tree — the picker must not offer what the server
     would refuse, and `hiddenTags` cascades to sub-tags (V0.43). */
  const hiddenTagIds = useMemo(
    () => buildHiddenSets(s.perm, s.tagEdges).tags,
    [s.perm, s.tagEdges],
  );

  const toggleTag = (id: TagId) => {
    const next = new Set(tags);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    touch(setTags)(next);
  };

  const save = () => {
    onClose();
    // Only the sections this popup actually DREW travel — an absent field must leave the
    // stored value alone, and must never enqueue a write family the server would refuse.
    // See `PopupDraft`.
    callAction('savePopup', node.id, {
      ...(structural ? { label, desc, color } : {}),
      ...(tagsEditable ? { tags: [...tags] } : {}),
      ai,
      ...(assignable && respSeedTrusted.current ? { responsibles, responsibleGroups } : {}),
      // Wrapped, so "the editor was not drawn" and "no plan" stay two different sentences.
      ...(assignable ? { docPlan: { plan: docPlan } } : {}),
      // Absent unless the field was actually LOADED — see `docPath` above.
      ...(structural && docPath ? { docPath } : {}),
    });
  };

  if (readOnly) {
    return (
      <>
        {head}
        <label>Description</label>
        {node.desc && node.desc.trim() ? (
          <div className="pp-readtext">{node.desc}</div>
        ) : (
          <div className="pp-readtext pp-empty">No description.</div>
        )}
        {/* NO RESPONSIBLES IN VIEW MODE (V0.49.1), by the user's decision. Who is accountable
            for a branch is an EDITING fact: it is acted on in edit mode, and in the reading
            shape it was a row of names between the description and the documentation —
            between somebody and the thing they opened the node to reach. The same reversal
            V0.44.1 made for the doc bar's ownership box, which is drawn only while the map's
            Edit toggle is on, and for the same reason. The editable body below still shows it,
            and `ReadResponsibles` is still what a participant without `canAssign` sees there. */}
        <label>Documentation</label>
        <PopupDocs node={node} s={s} readOnly />
        {/* TO-DOS (V0.50.1) — AFTER the documentation, which is where the edit shape puts it
            too: there it sits between the documentation location and the AI panel, and neither
            of those is drawn for a reader, so this is the same position with the undrawn
            things removed. Read-only here EXCEPT the reader's own checkboxes — being in
            View-only mode does not stop somebody reporting that they finished a job, which is
            the server's own rule rather than a second policy drawn here. And the description
            still opens on a click: that is the whole point of the expander for a reader.
            Renders nothing at all without VIEW_TODOS. */}
        <TodoSection node={node} s={s} readOnly />
        <label>Tags</label>
        <div className="tagpick" id="pp-tags">
          <ReadTags node={node} s={s} />
        </div>
        <div className="popup-foot">
          <button className="pbtn primary" id="pp-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {head}
      {/* The Name and Color lines are IN THE SEAT since V0.51 — see the `nameEditor` and
          `swatchInput` props above. What is left here is the case they never covered: a
          participant without EDIT_MAP, for whom renaming is somebody else's and an input whose
          save would be dropped is a lie with a caret in it. */}
      {structural ? null : (
        <>
          <label>Name</label>
          <div className="pp-readtext">{node.label}</div>
        </>
      )}
      <label>Description</label>
      {structural ? (
        <textarea
          id="pp-desc"
          placeholder="Add notes about this node…"
          value={desc}
          onChange={(e) => touch(setDesc)(e.target.value)}
        />
      ) : node.desc && node.desc.trim() ? (
        <div className="pp-readtext">{node.desc}</div>
      ) : (
        <div className="pp-readtext pp-empty">No description.</div>
      )}
      {/* RESPONSIBLES FOLD (V0.51). Who is accountable for a branch is a fact most visits to a
          node do not need, and the picker is 100px of token box under a heading. Shut, the
          heading names them; open, it is the picker it always was. */}
      {showResp ? (
        <PopupFold
          sectionKey={SECTION_RESPONSIBLES}
          id="pp-responsibles-panel"
          label="Responsibles"
          summary={{
            pills: respPills(node, s, assignable ? { responsibles, responsibleGroups } : null),
            plusClass: 'subj-tok mini',
          }}
          brief={<CountPill>{respCountLabel(node, s, assignable ? { responsibles, responsibleGroups } : null)}</CountPill>}
        >
          {assignable ? (
            <NodeSubjectPicker
              node={node}
              s={s}
              chosenUsers={responsibles}
              chosenGroups={responsibleGroups}
              onChangeUsers={touch(setResponsibles)}
              onChangeGroups={touch(setResponsibleGroups)}
            />
          ) : (
            <ReadResponsibles node={node} s={s} />
          )}
        </PopupFold>
      ) : null}
      {/* ONE DOCUMENTATION SECTION (V0.51). What is OWED and what EXISTS were two labelled
          blocks in a row — "Documentation plan" over the kind chips, then the slots — reading
          as two subjects when they are one, and already talking to each other (ticking a kind
          adds its row at once, V0.43.2). One heading, the plan on top of the rows it governs.
          The widget's address joins them at the foot as a single line rather than a full-width
          field under a heading of its own: it is where this node's documentation LIVES in the
          real application, which is a documentation fact, and it is edited about as often as
          anything in this popup ever is. */}
      <PopupFold
        sectionKey={SECTION_DOCS}
        id="pp-docs-panel"
        label="Documentation"
        summary={{ pills: docPills(node, s, assignable ? docPlan : null), plusClass: 'docpill mini' }}
        brief={<CountPill>{docCountLabel(node, assignable ? docPlan : null)}</CountPill>}
      >
        <>
          {assignable ? <DocPlanEditor plan={docPlan} onChange={touch(setDocPlan)} /> : null}
          {/* The plan DRAFT travels with it (V0.43.2), so ticking a kind adds its row at once.
              Only when the editor was drawn — `assignable` — or there is no draft to speak of. */}
          <PopupDocs
            node={node}
            s={s}
            readOnly={!structural}
            docPlan={assignable ? { plan: docPlan } : undefined}
          />
          {/* Behind EDIT_MAP rather than AI_ADMIN: this address decides which host-application
              page shows which documentation in the embeddable widget, so it is map structure,
              not an AI setting. The AI path it falls back to has the stricter gate, which is
              why the two are only legible together here. */}
          {structural ? (
            /* The RAW setter plus `onTouch`, not `touch(setDocPath)`: the field's own LOAD
               writes this draft, and that is the component correcting itself rather than
               somebody typing — see the two programmatic re-seeds above. */
            <DocPathField nodeId={node.id} draft={docPath} onChange={setDocPath} onTouch={onTouch} />
          ) : null}
        </>
      </PopupFold>
      {/* TO-DOS (V0.50.1) — BETWEEN the documentation location and the AI panel, by request.
          Both are `<details>` in the same form, so the two foldable blocks sit together and the
          plain fields above them read as one uninterrupted set. `readOnly={false}` here only
          decides whether the EDITING controls are drawn; the permission gate is the component's
          own, so a map editor without EDIT_TODOS still gets the read shape. */}
      <TodoSection node={node} s={s} readOnly={false} />
      {ai ? (
        <AiPanel
          node={node}
          nodes={s.nodes}
          edges={s.edges}
          cfg={s.aiDocCfgCache}
          targets={s.appTargets}
          targetsLoaded={s.appTargetsLoaded}
          draft={ai}
          onChange={touch(setAi)}
          canGenerate={capAi(s.caps, 'docgen')}
        />
      ) : null}
      {/* THE TAGS FOLD (V0.51). Shut, it states which tags the node carries as the chips
          themselves; open, it is the picker. Somebody without `ASSIGN_TAGS` gets the read
          chips inside the same fold rather than a second shape to learn. */}
      <PopupFold
        sectionKey={SECTION_TAGS}
        id="pp-tags-panel"
        label="Tags"
        summary={{ pills: tagPills(tags, s), plusClass: 'chip mini' }}
        brief={<CountPill>{tags.size === 1 ? '1 tag' : `${tags.size} tags`}</CountPill>}
      >
        {!tagsEditable ? (
          <div className="tagpick" id="pp-tags">
            <ReadTags node={node} s={s} />
          </div>
        ) : (
          <TagPicker
            tags={s.tags}
            tagEdges={s.tagEdges}
            selected={tags}
            onToggle={toggleTag}
            hiddenTags={hiddenTagIds}
            editing={inEditMode(s)}
            canEditTags={canEditTags(s.perm)}
          />
        )}
      </PopupFold>
      <div className="popup-foot">
        <button className="pbtn" id="pp-cancel" onClick={onClose}>
          Cancel
        </button>
        <button className="pbtn primary" id="pp-save" onClick={save}>
          Save
        </button>
      </div>
    </>
  );
}

/* -------------------------------- the documentation location in an app (V0.46) ------ *
 *                                                                                       *
 * WHERE THIS NODE'S DOCUMENTATION LIVES INSIDE THE REAL APPLICATION — `Node.docPath`,    *
 * the address the embeddable widget resolves a host page's URL against. Behind           *
 * `EDIT_MAP`, because it decides what a public credential publishes to another           *
 * application, which is map structure rather than an AI setting.                         *
 *                                                                                       *
 * IT LOADS LAZILY, ON FIRST INTERACTION, AND NEVER ON OPEN. `docPath` is deliberately    *
 * not on the `/api/map` payload (the widget's wire contract forbids it, and `appPath` is  *
 * AI_ADMIN's there), so the value has to be fetched per node — and two specs count the   *
 * requests one popup open makes. A section that fetched on open would put a request on   *
 * every popup ever opened, for a field almost nobody edits; it is the same accident       *
 * `ReviewControl`'s `seed` prop exists to prevent, and `GET /api/nodes/:id/doc-path`'s   *
 * own header asks for exactly this. So the box is READ-ONLY until it is touched: focus    *
 * it and it loads, which means a keystroke can never be swallowed by a reply landing on  *
 * top of it.                                                                             *
 * ------------------------------------------------------------------------------------- */

function DocPathField({
  nodeId,
  draft,
  onChange,
  onTouch,
}: {
  nodeId: NodeId;
  draft: { path: string | null; from: string | null } | null;
  /** The RAW setter: loading is this component correcting itself, not somebody typing. */
  onChange: (next: { path: string | null; from: string | null }) => void;
  /** "Somebody edited this." Called only for a real edit — see `touch` in `PopupBody`. */
  onTouch: () => void;
}) {
  const s = useAppState();
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'failed'>(
    draft ? 'ready' : 'idle',
  );
  const [help, setHelp] = useState<{
    appPath: string | null;
    effectiveAppPath: string | null;
    effectiveAppPathNodeId: NodeId | null;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Asked already? One request per popup, however many times the box is focused. */
  const asked = useRef(!!draft);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );

  const load = () => {
    if (asked.current) return;
    asked.current = true;
    setState('loading');
    void getNodeDocPath(nodeId).then(
      (d) => {
        if (!alive.current) return;
        setHelp(d);
        // NOT through `touch`: nothing has been edited yet, and marking the popup dirty here
        // would route the next `open()` of this node to `refresh()` over an untouched form.
        onChange({ path: d.docPath, from: d.docPath });
        setState('ready');
        // The box was read-only a moment ago, so it lost nothing — but it did lose the caret
        // the person had just put in it.
        requestAnimationFrame(() => inputRef.current?.focus());
      },
      () => {
        if (alive.current) setState('failed');
      },
    );
  };

  const ready = state === 'ready' && !!draft;
  const sourceLabel =
    help?.effectiveAppPathNodeId && help.effectiveAppPathNodeId !== nodeId
      ? s.nodes[help.effectiveAppPathNodeId]?.label
      : null;

  /* ONE LINE AT THE FOOT OF THE SECTION (V0.51), not a full-width field under a heading of its
     own. It is where this node's documentation LIVES in the real application — a documentation
     fact, which is why it moved INSIDE the section rather than sitting beside it — and it is
     edited about as often as anything in this popup ever is. The label is inline and the field
     takes what is left; the help line below is unchanged, and so is loading on first focus. */
  return (
    <div className="pp-docloc">
      <label htmlFor="pp-docpath">Shown in the app at</label>
      <input
        id="pp-docpath"
        ref={inputRef}
        className="fld"
        type="text"
        spellCheck={false}
        autoComplete="off"
        placeholder={
          state === 'loading'
            ? 'Loading…'
            : ready
              ? '/users'
              : 'Click to load this node’s documentation location'
        }
        readOnly={!ready}
        value={ready ? (draft?.path ?? '') : ''}
        onFocus={load}
        onChange={(e) => {
          onTouch();
          // Blank is UNSET, not "explicitly nothing" — the server and the resolver both read
          // it that way, so the three ends agree about what an empty box means.
          const v = e.target.value;
          onChange({ path: v.trim() ? v : null, from: draft?.from ?? null });
        }}
      />
      <div className="pp-readtext pp-empty" id="pp-docpath-help" style={{ fontSize: 12.5, marginTop: 4 }}>
        {state === 'failed'
          ? 'Could not load this node’s documentation location.'
          : state === 'loading'
            ? 'Loading…'
            : !ready
              ? 'The address inside the application whose page should show this documentation.'
              : draft?.path
                ? help?.effectiveAppPath
                  ? `Overrides the AI path: ${help.effectiveAppPath}${sourceLabel ? ` (from ${sourceLabel})` : ''}`
                  : 'Set here. There is no AI path on this node or its ancestors.'
                : help?.effectiveAppPath
                  ? `Empty — falls back to the AI path: ${help.effectiveAppPath}${sourceLabel ? ` (from ${sourceLabel})` : ''}`
                  : 'Empty, and there is no AI path to fall back to — no host address resolves to this node.'}
      </div>
    </div>
  );
}

/* --------------------------------------------------- the responsibles rows (V0.37) --- *
 *                                                                                        *
 * IN THE BODY, NOT THE HEADER — the same placement the owner row had, above               *
 * Documentation, because these people are responsible for the documentation below it.     *
 * The read rows keep the owner row's classes (`.pp-ownertext`, `.pp-empty`) so the       *
 * visual weight is the one `node-popup.png` photographs.                                  *
 * ------------------------------------------------------------------------------------- */

/**
 * Who is responsible, read-only: the EFFECTIVE list — direct rows plus everything inherited
 * down from ancestors, each inherited entry saying which ancestor it flows from. Ids resolve
 * to names through the directory; an id it cannot name belongs to an account whose
 * eligibility the server would have filtered, so in practice an unresolved id is a directory
 * still loading — and until it has, nothing is claimed.
 *
 * DIRECT groups (V0.37.1) render as their own rows, above the people — `node.responsibleGroups`
 * lists them, already dormant-filtered server-side. A person covered by one of THIS node's own
 * groups says so ("via Documentation Team"); a person covered by a group assigned higher up
 * shows only as inherited — the ancestor's own popup is where that group's chip lives.
 */
function ReadResponsibles({ node, s }: { node: MapNode; s: AppSnapshot }) {
  const sources = responsibleSources(node.id, s.nodes, s.edges);
  const groupIds = node.responsibleGroups ?? [];
  if (!sources.size && !groupIds.length) return <div className="pp-ownertext pp-empty">Unassigned.</div>;
  if (!s.userDirectoryLoaded) return <div className="pp-ownertext pp-empty">Loading…</div>;
  const covered = coveredByGroup(groupIds, s.groupDirectory);
  return (
    <>
      {groupIds.map((gid) => {
        const g = s.groupDirectory.find((x) => x.id === gid);
        return (
          <div className="pp-ownertext" key={'g:' + gid}>
            {g?.name ?? gid}
            <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}> — group</span>
          </div>
        );
      })}
      {[...sources.entries()].map(([userId, sourceId]) => {
        const name = s.userDirectory.find((u) => u.id === userId)?.displayName ?? userId;
        const inherited = sourceId !== node.id;
        const viaGroup = !inherited ? covered.get(userId) : undefined;
        return (
          <div className="pp-ownertext" key={userId}>
            {name}
            {inherited ? (
              <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
                {' '}
                — inherited from {s.nodes[sourceId]?.label ?? 'an ancestor'}
              </span>
            ) : viaGroup ? (
              <span style={{ fontSize: 12, color: 'var(--demo-color)' }}> — via {viaGroup}</span>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

/**
 * The node popup's responsibles picker — the shared `SubjectPicker` (components/) with the
 * NODE eligibility flags, plus this node's inherited rows below it. The picker itself is the
 * one implementation both this row and the doc bar's popover render; what is node-shaped
 * here is only which flag filters the lists and where the inherited entries come from.
 *
 * DIRECT rows only in the picker. Inherited responsibles (people or groups) are an ancestor's
 * to revoke, so they render below as read-only "inherited from" rows, never as removable
 * chips.
 */
function NodeSubjectPicker({
  node,
  s,
  chosenUsers,
  chosenGroups,
  onChangeUsers,
  onChangeGroups,
}: {
  node: MapNode;
  s: AppSnapshot;
  chosenUsers: string[];
  chosenGroups: string[];
  onChangeUsers: (next: string[]) => void;
  onChangeGroups: (next: string[]) => void;
}) {
  const userSources = responsibleSources(node.id, s.nodes, s.edges);
  const inheritedUsers = [...userSources.entries()].filter(([, src]) => src !== node.id);
  // Groups inherit the SAME way user rows do — each ancestor's own `responsibleGroups` is a
  // direct row there, so a group assigned higher up is this node's inherited entry too.
  const inheritedGroups = useMemo(() => {
    const out: [string, NodeId][] = [];
    const seen = new Set<string>();
    let cur: NodeId | undefined = node.id;
    let guard = 0;
    while (cur !== undefined && guard++ < 10_000) {
      if (cur !== node.id) {
        for (const gid of s.nodes[cur]?.responsibleGroups ?? []) {
          if (!seen.has(gid)) {
            seen.add(gid);
            out.push([gid, cur]);
          }
        }
      }
      cur = s.edges[cur];
    }
    return out;
  }, [node.id, s.nodes, s.edges]);

  if (!s.userDirectoryLoaded || !s.groupDirectoryLoaded) {
    return <div className="pp-ownertext pp-empty">Loading the directory…</div>;
  }

  return (
    <div>
      <SubjectPicker
        id="pp-responsibles"
        users={s.userDirectory}
        groups={s.groupDirectory}
        eligibleUser={(u) => u.canBeNodeResponsible}
        eligibleGroup={(g) => g.canBeNodeResponsible}
        chosenUsers={chosenUsers}
        chosenGroups={chosenGroups}
        onChangeUsers={onChangeUsers}
        onChangeGroups={onChangeGroups}
        emptyUsersText="Nobody is eligible — a responsible needs the NODE_RESPONSIBLE permission."
        emptyGroupsText="No group is eligible yet."
      />

      {inheritedGroups.map(([gid, sourceId]) => {
        const g = s.groupDirectory.find((x) => x.id === gid);
        return (
          <div className="pp-ownertext" key={'ig:' + gid} style={{ marginTop: 2 }}>
            {g?.name ?? gid}
            <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
              {' '}
              — group, inherited from {s.nodes[sourceId]?.label ?? 'an ancestor'}
            </span>
          </div>
        );
      })}
      {inheritedUsers.map(([userId, sourceId]) => (
        <div className="pp-ownertext" key={userId} style={{ marginTop: 2 }}>
          {s.userDirectory.find((u) => u.id === userId)?.displayName ?? userId}
          <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
            {' '}
            — inherited from {s.nodes[sourceId]?.label ?? 'an ancestor'}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------- the documentation plan (V0.38) --- *
 *                                                                                       *
 * WHAT THIS NODE OWES: the denominator the clearance heatmap divides by. Three-state,   *
 * and all three are drawable — `null` ("No plan": nothing promised, nothing missing),   *
 * `{}` ("No documentation expected": a deliberate zero), and a plan of kind chips plus  *
 * a custom-docs count. In the popup's EDIT shape only, which is not photographed        *
 * (`node-popup.png` shoots the read-only shape), behind `canAssign` — planning a        *
 * node's documentation is the same authority as distributing it, the server's gate.    *
 * The chips are the tag picker's vocabulary, tinted with each slot's own colour.        *
 * ------------------------------------------------------------------------------------ */

const PLAN_KINDS: readonly { key: keyof DocPlan & string; label: string; color: string }[] = [
  { key: 'documentation', label: 'Documentation', color: DOC_BEAN_COLOR.link },
  { key: 'course', label: 'Course', color: DOC_BEAN_COLOR.linkCourse },
  { key: 'internal', label: 'Internal', color: DOC_BEAN_COLOR.linkInternal },
  { key: 'demo', label: 'Demo', color: DOC_BEAN_COLOR.linkDemo },
];

function DocPlanEditor({
  plan,
  onChange,
}: {
  plan: DocPlan | null;
  onChange: (next: DocPlan | null) => void;
}) {
  const nothingExpected = plan !== null && Object.keys(plan).length === 0;
  const customCount = plan?.custom ?? 0;

  /**
   * What the plan said before "Nothing expected" emptied it (V0.42.4).
   *
   * "Nothing expected" IS the empty plan, so pressing it has to clear the kinds — a node
   * cannot both owe a Course and owe nothing. Pressing it again used to drop the node to NO
   * PLAN AT ALL, silently losing what had been chosen, which makes a mis-click expensive. The
   * kinds come back instead, for as long as this popup is open; the popup is keyed on the open
   * (see the region's header), so closing without saving forgets them, which is the same
   * lifetime every other draft here has.
   */
  const beforeExempt = useRef<DocPlan | null>(null);

  const toggleExempt = () => {
    if (nothingExpected) {
      const back = beforeExempt.current;
      beforeExempt.current = null;
      onChange(back && Object.keys(back).length ? { ...back } : null);
      return;
    }
    // Unconditional, so the memory can never outlive the press that stored it: a plan emptied
    // by some other route used to leave the previous one here, and the NEXT un-exempting put
    // back a plan the node had already moved on from.
    beforeExempt.current = plan && Object.keys(plan).length ? { ...plan } : null;
    onChange({});
  };

  /**
   * What a kind press or a custom step commits.
   *
   * AN EMPTIED PLAN IS `null`, NOT `{}` (V0.48.1, at the user's request). Unticking the last
   * kind used to leave the empty object behind — and the empty object IS "No documentation
   * expected", so taking the last kind off a node silently promoted it to a deliberate
   * exemption, red chip and all. Nobody said that. "No plan" is where the node was before
   * anything was ticked, and it is where taking everything off puts it back.
   *
   * The reverse direction needs no code at all: a kind pressed while exempt spreads `{}` and
   * lands on `{kind:true}`, which is not exempt. It was unreachable only because the chips
   * were `pointer-events:none` — see `.chip.off` in the stylesheet.
   */
  const commit = (next: DocPlan) => {
    beforeExempt.current = null;
    onChange(Object.keys(next).length ? next : null);
  };

  const toggleKind = (key: 'documentation' | 'internal' | 'course' | 'demo') => {
    const next: DocPlan = { ...(plan ?? {}) };
    if (next[key]) delete next[key];
    else next[key] = true;
    commit(next);
  };
  const setCustom = (raw: string) => {
    const n = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
    const next: DocPlan = { ...(plan ?? {}) };
    if (n >= 1) next.custom = n;
    else delete next.custom;
    commit(next);
  };

  return (
    <div id="pp-docplan">
      <div className="tagpick" style={{ marginBottom: 6 }}>
        {PLAN_KINDS.map((k) => {
          const on = plan !== null && !!plan[k.key];
          return (
            <span
              className={`chip${on ? ' on' : ''}${nothingExpected ? ' off' : ''}`}
              key={k.key}
              data-plankind={k.key}
              style={on ? { background: k.color } : undefined}
              onClick={() => toggleKind(k.key as 'documentation' | 'internal' | 'course' | 'demo')}
            >
              <span className="cd" style={{ background: k.color }} />
              {k.label}
            </span>
          );
        })}
        {/* HOW MANY CUSTOM DOCUMENTS, stepped from either side (V0.42.5). The count is the
            only number in this row, so the "×" said nothing the label did not; and a native
            number spinner stacks its arrows in a 22px-tall chip, where they are 6px each. */}
        <span className={`chip ro plan-custom${nothingExpected ? ' off' : ''}`}>
          <button
            type="button"
            className="plan-step"
            id="pp-plan-custom-down"
            aria-label="One fewer custom document"
            disabled={customCount <= 0}
            onClick={() => setCustom(String(customCount - 1))}
          >
            −
          </button>
          <span className="plan-custom-lbl">Custom</span>
          <input
            id="pp-plan-custom"
            type="text"
            inputMode="numeric"
            aria-label="Custom documents expected"
            value={customCount || ''}
            placeholder="0"
            onChange={(e) => setCustom(e.target.value)}
          />
          <button
            type="button"
            className="plan-step"
            id="pp-plan-custom-up"
            aria-label="One more custom document"
            disabled={customCount >= 100}
            onClick={() => setCustom(String(customCount + 1))}
          >
            +
          </button>
        </span>
        {/* THE FIFTH THING A PLAN CAN SAY, so it sits with the other four rather than under
            them (V0.42.4, chosen from the POC). Red because it is the one choice that
            overrides the rest, and it visibly dims them — dims, not disables: since V0.48.1
            pressing one is how you change your mind, and it lifts the exemption. */}
        <span
          className={`chip plan-none${nothingExpected ? ' on' : ''}`}
          id="pp-plan-nodoc"
          data-plankind="none"
          role="checkbox"
          aria-checked={nothingExpected}
          tabIndex={0}
          onClick={toggleExempt}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleExempt();
            }
          }}
        >
          No documentation expected
        </span>
      </div>
      {/* Only the two states the chips cannot show for themselves: no plan at all, and the
          deliberate empty one. A row of lit chips does not need a sentence saying it is lit
          (V0.42.5). */}
      {plan === null || nothingExpected ? (
        <div className="pp-readtext pp-empty" id="pp-plan-hint" style={{ marginTop: 4 }}>
          {plan === null
            ? // Says what to DO about it (V0.45). "This node promises nothing yet" is true and
              // was the whole message, so a node with no plan showed a plan editor, no
              // document section, and no hint that the two were connected — the walkthrough
              // spent a while looking for the missing documentation controls.
              'No plan — this node promises nothing yet. Tick a kind above to plan a document; ' +
              'it appears below as a dashed pill you can click to create the page.'
            : 'Nothing expected: this node deliberately owes no documentation.'}
        </div>
      ) : null}
      {plan !== null ? (
        <button type="button" className="pbtn" id="pp-plan-clear" onClick={() => onChange(null)}>
          Clear plan
        </button>
      ) : null}
    </div>
  );
}

/**
 * The assigned tags, non-interactive.
 *
 * INVISIBLE TAGS STAY HIDDEN here too, not just in the sidebar tree — a tag marked invisible
 * is structure an editor uses, and listing it on a node would leak it to everybody else. Edit
 * mode shows them, because that is who they are for.
 */
/** The draft the Responsibles fold summarises, or `null` for a reader who has no draft. */
interface RespDraft {
  responsibles: string[];
  responsibleGroups: string[];
}

/**
 * Who the Responsibles fold names while shut: the groups, then the people, then the ones
 * INHERITED from an ancestor — dashed, the language the doc bar's ownership box already uses
 * for "theirs, not this node's".
 *
 * From the DRAFT when the reader may edit it, so a person added reaches the heading before Save
 * does; from the node when they may not. Inherited entries come from the ancestor walk either
 * way, because they are not in anybody's draft to begin with.
 */
function respPills(node: MapNode, s: AppSnapshot, draft: RespDraft | null) {
  const directUsers = draft ? draft.responsibles : (node.responsibles ?? []);
  const groupIds = draft ? draft.responsibleGroups : (node.responsibleGroups ?? []);
  const covered = coveredByGroup(groupIds, s.groupDirectory);
  const inherited = [...responsibleSources(node.id, s.nodes, s.edges).entries()]
    .filter(([uid, src]) => src !== node.id && !directUsers.includes(uid) && !covered.has(uid))
    .map(([uid]) => uid);
  const tok = (key: string, name: string, cls: string, title?: string) => (
    <span className={`subj-tok mini${cls}`} key={key} title={title ?? name}>
      <span className={`subj-av${cls.includes('grp') ? ' grp' : ''}`}>{initialsOf(name)}</span>
      {name}
    </span>
  );
  return [
    ...groupIds.map((gid) =>
      tok(`g:${gid}`, s.groupDirectory.find((x) => x.id === gid)?.name ?? gid, ' grp'),
    ),
    ...directUsers.map((uid) =>
      tok(uid, s.userDirectory.find((u) => u.id === uid)?.displayName ?? uid, ''),
    ),
    ...inherited.map((uid) => {
      const name = s.userDirectory.find((u) => u.id === uid)?.displayName ?? uid;
      return tok(`i:${uid}`, name, ' inh', `${name} — inherited`);
    }),
  ];
}

/** The same fact as a figure, for the heading of a section whose rows are on screen. */
function respCountLabel(node: MapNode, s: AppSnapshot, draft: RespDraft | null): string {
  const parts = respPills(node, s, draft).length;
  if (!parts) return 'unassigned';
  return parts === 1 ? '1 responsible' : `${parts} responsibles`;
}

/** The four fixed slots as {kind, id field}, in the order the section draws them. */
const DOC_SLOTS = [
  { perm: 'documentation', field: 'docId' },
  { perm: 'internal', field: 'internalDocId' },
  { perm: 'course', field: 'courseDocId' },
  { perm: 'demo', field: 'demoDocId' },
] as const;

/**
 * What the Documentation fold states while shut: the pages that EXIST, filled in their kind's
 * colour, then the ones the plan OWES, dashed in the same colour.
 *
 * The same two pills the section itself draws, at summary size — `.docpill.planned`'s dashed
 * outline is the "this is owed" language every surface in the app already speaks (V0.43.2), so
 * the heading says "two written, one still to write" without a word for it.
 *
 * `plan` is the DRAFT when the reader may edit it, so ticking a kind adds its owed pill to the
 * heading at once — and `null` for somebody who may not, whose plan is not theirs to read off a
 * draft they do not have.
 */
function docPills(node: MapNode, s: AppSnapshot, plan: DocPlan | null) {
  const out: React.ReactNode[] = [];
  const pill = (key: string, color: string, label: string, owed: boolean) => (
    <span
      className={`docpill mini${owed ? ' planned' : ''}`}
      key={key}
      style={owed ? { borderColor: color, color } : { background: color }}
      title={label}
    >
      <svg className="ico" aria-hidden="true">
        <use href="#i-doc" />
      </svg>
      {label}
    </span>
  );
  for (const slot of DOC_SLOTS) {
    const id = node[slot.field];
    if (!id) continue;
    const label = s.docTitles[id] || DOC_KINDS.find((k) => k.perm === slot.perm)?.label || 'Untitled';
    out.push(pill(slot.field, kindColorOf(slot.perm), label, false));
  }
  for (const id of node.customDocs || []) {
    out.push(pill(`c:${id}`, DOC_BEAN_COLOR.link, s.docTitles[id] || 'Untitled', false));
  }
  const owed = plannedMissing({ ...node, expectedDocs: plan }, () => true);
  for (const kind of owed.kinds) {
    const label = DOC_KINDS.find((k) => k.perm === kind)?.label ?? kind;
    out.push(pill(`owed:${kind}`, kindColorOf(kind), OWED_PILL_LABEL(label), true));
  }
  for (let i = 0; i < owed.customMissing; i += 1) {
    out.push(pill(`owed:custom:${i}`, kindColorOf('custom'), OWED_PILL_LABEL('Custom'), true));
  }
  return out;
}

/** The same fact as a figure, for the heading of a section whose contents are on screen. */
function docCountLabel(node: MapNode, plan: DocPlan | null): string {
  const have =
    DOC_SLOTS.filter((sl) => node[sl.field]).length + (node.customDocs?.length ?? 0);
  const owed = plannedMissing({ ...node, expectedDocs: plan }, () => true);
  const missing = owed.kinds.length + owed.customMissing;
  if (!have && !missing) return 'none';
  if (!missing) return have === 1 ? '1 page' : `${have} pages`;
  return `${have} of ${have + missing} present`;
}

/**
 * The tags a fold's summary states while shut — the chips themselves, at summary size.
 *
 * The node's OWN colours, because a tag is recognised by its colour before its name; `.mini` is
 * only a size. `PopupFold` caps the list at four and adds the "+n", so this hands over all of
 * them in the vocabulary's display order rather than the order they were clicked.
 */
function tagPills(chosen: ReadonlySet<TagId>, s: AppSnapshot) {
  return Object.values(s.tags)
    .filter((t) => chosen.has(t.id))
    .map((t) => (
      <span className="chip on mini" key={t.id} style={{ background: t.color }}>
        <span className="cd" style={{ background: t.color }} />
        {t.name}
      </span>
    ));
}

function ReadTags({ node, s }: { node: MapNode; s: AppSnapshot }) {
  const chosen = new Set(node.tags || []);
  const mine = Object.values(s.tags).filter((t) => chosen.has(t.id) && !t.invisible);
  if (!mine.length) {
    return <span style={{ fontSize: 13, color: 'var(--ink-faint)' }}>No tags assigned.</span>;
  }
  return (
    <>
      {mine.map((t) => (
        <span className="chip on ro" key={t.id} style={{ background: t.color }}>
          <span className="cd" style={{ background: t.color }} />
          {t.name}
        </span>
      ))}
    </>
  );
}
